import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { LoanStatusRow } from 'app/components/loan-status-row';
import { PCImage } from 'app/design/primitives/image';
import React from 'react';
import { View } from 'react-native';
import { useTheme } from '@shopify/restyle';
import type { Theme } from 'app/shopify';
import type { Loan } from 'app/types/loan';
import type { User } from 'app/types/user';
import { useTranslation } from 'react-i18next';

import { localeAwareFormat } from 'app/lib/date';
import { LoanType } from 'app/types/loan';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { getLoanInfos } from '../functions';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import type { TFunction } from 'i18next';
import { LoanMenu } from './loan-menu';
import { PeriodTimeline } from './period-timeline';

const ucFirst = (str: string) => (!str ? undefined : str.charAt(0).toUpperCase() + str.slice(1));

function getOtherUserRole(isSell: boolean, isDonation: boolean, loanType?: LoanType) {
  if (isSell) {
    return loanType === LoanType.Lending ? 'buyer' : 'seller';
  }
  if (isDonation) {
    return loanType === LoanType.Lending ? 'recipient' : 'donor';
  }
  return loanType === LoanType.Lending ? 'borrower' : 'lender';
}

function Counterpart({ otherUser, role, t }: { otherUser?: Loan['borrower']; role: string; t: TFunction<'one-loan'> }) {
  if (!otherUser) {
    return null;
  }

  const profile = otherUser.profile;
  const nameParts: string[] = [];
  if (profile?.firstName) nameParts.push(profile.firstName);
  if (profile?.lastName) nameParts.push(profile.lastName);
  const joinedName = nameParts.join(' ');
  const otherUserName = joinedName.length > 0 ? joinedName : t('deletedUser', { ns: 'common' });

  return (
    <Row alignItems="center" gap="spacing.general.x2">
      {profile?.picture ? (
        <PCImage
          aspectRatio={1}
          height={24}
          width={24}
          source={{ uri: profile.picture }}
          style={{ borderRadius: 12 }}
        />
      ) : null}
      <Column flex={1} gap="spacing.general.x0,5">
        <Text color="text.secondary" variant="bodySmall">
          {t(`counterpart.${role}`)}
        </Text>
        <Text variant="bodySmallMedium" style={{ flexShrink: 1 }}>
          {otherUserName}
        </Text>
      </Column>
    </Row>
  );
}

export function Head({ loan, user }: { loan?: Loan | null; user?: User }) {
  const { t } = useTranslation('one-loan');
  const theme = useTheme<Theme>();
  const isService = loan?.listing.subtype === ListingSubtype.service;
  const isDonation = isDonationListing(loan?.listing);
  const isSell = loan?.listing.transactionMode === TransactionMode.sell;
  const loanInfos = user ? getLoanInfos(loan, user) : undefined;
  const otherUser = loanInfos?.otherUser;
  const otherUserRole = getOtherUserRole(isSell, isDonation, loanInfos?.loanType);

  if (!loan) {
    return null;
  }

  const localizedStart =
    localeAwareFormat(new Date(loan.start), t('dateFormat_MMMMd', { ns: 'common' })) +
    (loan.listing.timeStart ? `, ${loan.listing.timeStart}` : '');

  return (
    <Column
      style={{ gap: theme.spacing['spacing.general.x4'] }}
      padding="spacing.general.x4"
      backgroundColor="surface.default"
      borderWidth={1}
      borderColor="border.secondary"
      borderRadius="radius.lg"
    >
      {user ? (
        <View style={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <LoanMenu loan={loan} user={user} />
        </View>
      ) : null}
      <Row alignItems="flex-start" gap="spacing.general.x3">
        <ListingThumbnail {...loan.listing} size={72} skipDonationOverlay />
        <Column flex={1} gap="spacing.general.x1">
          <Text numberOfLines={2} variant="bodyLargeStrong" style={{ paddingRight: user ? 40 : 0 }}>
            {loan.listing.item.name}
          </Text>
          <Counterpart otherUser={otherUser} role={otherUserRole} t={t} />
        </Column>
      </Row>

      {isSell || isDonation ? (
        <LoanStatusRow
          end={loan.end}
          isDonation={isDonation}
          listingSubtype={loan.listing.subtype}
          returned={loan.returned}
          showClock={false}
          presentation="summary"
          start={loan.start}
          status={loan.status}
          transactionMode={loan.listing.transactionMode}
        />
      ) : null}

      <Column gap="spacing.general.x2">
        {isSell || isDonation ? (
          <Column gap="spacing.general.x1">
            <Text variant="bodySmall" color="text.secondary">
              {t(isDonation ? 'donationDate' : 'transactionDate')}
            </Text>
            <Text variant="bodyBaseMedium">{ucFirst(localizedStart)}</Text>
          </Column>
        ) : (
          <PeriodTimeline loan={loan} isService={isService} />
        )}
        {loan.flexibleDates ? (
          <Text variant="bodySmall" color="text.secondary">
            {t('noDateRange', { ns: 'all-listings' })}
          </Text>
        ) : null}
      </Column>
    </Column>
  );
}
